import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatValue } from "@/lib/chartTheme";

// Item 19 (2.1.1 / 4.1.2): keyboard navigation between chart data points.
// A transparent, focusable overlay sits on the chart (pointer-events: none so the mouse still
// reaches ECharts). Tab focuses it, arrow keys walk the points, Up/Down switch series, Enter
// activates the point (cross-filter / drilldown / drillthrough — whatever a click does) and Esc
// releases focus. Every move highlights the point in the chart and is announced via aria-live.
export function ChartKeyboardNav({ widget, data, type, chartRef, onActivate, label }) {
  const rows = data?.rows || [];
  const columns = data?.columns || [];
  const meta = data?.meta || {};
  const measures = useMemo(() => (meta.measures || []).filter((m) => m?.key), [meta.measures]);
  const dims = useMemo(() => (meta.dimensions || []).filter((d) => d?.key), [meta.dimensions]);
  const measKeys = measures.length ? measures.map((m) => m.key) : columns.filter((c) => rows.some((r) => typeof r[c] === "number"));
  const dimKeys = dims.length ? dims.map((d) => d.key) : columns.filter((c) => !measKeys.includes(c));
  const [pos, setPos] = useState({ i: 0, s: 0 });
  const [active, setActive] = useState(false);
  const [msg, setMsg] = useState("");
  const hostRef = useRef(null);
  const total = rows.length;

  const inst = useCallback(() => {
    try { return chartRef?.current?.getEchartsInstance?.() || null; } catch { return null; }
  }, [chartRef]);

  const announce = useCallback((i, s) => {
    const row = rows[i];
    if (!row) return;
    const mk = measKeys[s] || measKeys[0];
    const m = measures.find((x) => x.key === mk);
    const dimPart = dimKeys.map((k) => `${dims.find((d) => d.key === k)?.label || String(k).replace(/_/g, " ")}: ${row[k] ?? "—"}`).join(", ");
    const valPart = mk ? `${m?.label || String(mk).replace(/_/g, " ")}: ${formatValue(row[mk], m?.format)}` : "";
    setMsg(`${dimPart}${dimPart && valPart ? ", " : ""}${valPart}. Point ${i + 1} of ${total}.`);
  }, [rows, measKeys, dimKeys, measures, dims, total]);

  const focusPoint = useCallback((i, s) => {
    const chart = inst();
    if (chart) {
      try {
        chart.dispatchAction({ type: "downplay" });
        chart.dispatchAction({ type: "highlight", seriesIndex: s, dataIndex: i });
        chart.dispatchAction({ type: "showTip", seriesIndex: s, dataIndex: i });
      } catch { /* series/data index not addressable on this chart type */ }
    }
    announce(i, s);
  }, [inst, announce]);

  // Release the highlight when focus leaves so the chart isn't left in a highlighted state
  useEffect(() => {
    if (active) return undefined;
    const chart = inst();
    try { chart?.dispatchAction({ type: "hideTip" }); chart?.dispatchAction({ type: "downplay" }); } catch { /* noop */ }
    return undefined;
  }, [active, inst]);

  const onKeyDown = (e) => {
    if (!total) return;
    const nSeries = Math.max(1, measKeys.length);
    let { i, s } = pos;
    const k = e.key;
    if (k === "ArrowRight" || k === "ArrowDown" || k === "ArrowLeft" || k === "ArrowUp" || k === "Home" || k === "End" || k === "Enter" || k === " " || k === "Escape") e.preventDefault();
    if (k === "ArrowRight") i = (i + 1) % total;
    else if (k === "ArrowLeft") i = (i - 1 + total) % total;
    else if (k === "ArrowDown") s = (s + 1) % nSeries;
    else if (k === "ArrowUp") s = (s - 1 + nSeries) % nSeries;
    else if (k === "Home") i = 0;
    else if (k === "End") i = total - 1;
    else if (k === "Enter" || k === " ") {
      const row = rows[pos.i];
      const dim0 = dimKeys[0];
      onActivate?.({
        name: dim0 ? row?.[dim0] : undefined,
        value: measKeys[pos.s] ? row?.[measKeys[pos.s]] : undefined,
        seriesName: measures.find((m) => m.key === measKeys[pos.s])?.label || measKeys[pos.s],
        dataIndex: pos.i, seriesIndex: pos.s, data: row, keyboard: true,
      }, hostRef.current);
      return;
    } else if (k === "Escape") { hostRef.current?.blur(); return; }
    else return;
    setPos({ i, s });
    focusPoint(i, s);
  };

  if (!total || !measKeys.length) return null;
  const hint = `${label || widget?.title || type} chart. ${total} data points. Use left and right arrow keys to move between points${measKeys.length > 1 ? ", up and down to change series" : ""}, Enter to select.`;
  return (
    <>
      <div ref={hostRef} data-testid="chart-keynav" tabIndex={0} role="application" aria-label={hint} aria-describedby={`keynav-live-${widget?.id || "w"}`}
        onKeyDown={onKeyDown} onFocus={() => { setActive(true); focusPoint(pos.i, pos.s); }} onBlur={() => setActive(false)}
        className={"absolute inset-0 z-[15] pointer-events-none rounded-xl transition-[box-shadow] " + (active ? "ring-2 ring-[color:var(--brand-ocean)] ring-offset-0" : "")}>
        {active && (
          <span data-testid="chart-keynav-badge" className="absolute top-1 right-1 px-1.5 py-0.5 rounded-full bg-[color:var(--surface)]/95 border border-[color:var(--glass-border)] text-[9px] text-[color:var(--ink-3)]">
            {pos.i + 1}/{total}
          </span>
        )}
      </div>
      <span id={`keynav-live-${widget?.id || "w"}`} data-testid="chart-keynav-live" role="status" aria-live="polite" className="sr-only">{msg}</span>
    </>
  );
}
